import { useOktaAuth } from '@okta/okta-react';
import { useEffect } from "react"
import { Route, Routes } from "react-router-dom";
import { fetchAllProviders, fetchMarkets } from "../../../api";
import { isOpsManager } from '../../../okta';
import { DocumentTitle } from '../../core/DocumentTitle';
import { groupCodeToName } from "../../scheduling/Util";

import PodPage from "../PodPage";
import PodPlaceholder from '../PodPlaceholder';
import PodSearchPage from "../PodSearchPage";
import { getProviderCategory } from '../Util';

import { setLoadError, setMarketRegions, setProviderList, setReady, setUserIsManager } from "./actions";
import { PodStateContext, ProviderOption, usePodReducer } from "./reducer";
import { getLoadError } from './selectors';

const PodRouter = () => {
  const {useSelector, dispatch} = usePodReducer();
  const { authState } = useOktaAuth();

  const loadError: string | undefined = useSelector(getLoadError);

  useEffect(() => {
    /**
     * Query the list of markets once, then store the resulting regions and markets before
     * signaling Ready. This allows one load of the markets list to be used on the search page as
     * well as the pod edit page. Do the same as above for the provider list. I suspect we can
     * obtain this from saga by wrapping the router in another redux provider, but for the MVP,
     * I'm fetching separately for simplicity.
     */
    const marketFetch = fetchMarkets().then((markets) => {
      const activeMarkets = markets.filter(market => market.active && market.region !== null);
      const regions = Array.from(new Set(activeMarkets.map(market => market.region)));

      const seenGroups: Set<string> = new Set();
      const marketGroupList: {[key: string]: { text: string, region: string, markets: string[] }} = {};

      for (let i = 0; i < activeMarkets.length; i++) {
        const thisMarket = activeMarkets[i];
        const thisGroupCode = thisMarket.groupCode;

        if (seenGroups.has(thisGroupCode)) {
          marketGroupList[thisGroupCode].markets.push(thisMarket.code);
          continue;
        }

        let name = groupCodeToName(thisGroupCode, false);
        if (name === 'Unknown Group') {
          name = thisGroupCode;
        }

        marketGroupList[thisGroupCode] = {
          text: name,
          region: thisMarket.region,
          markets: [thisMarket.code],
        };

        seenGroups.add(thisGroupCode);
      }

      const marketGroups = Array.from(seenGroups).map(group => ({
        ...marketGroupList[group],
        key: group
      }));

      dispatch(setMarketRegions(
        marketGroups,
        regions,
      ));
    }).catch(reason => {
      dispatch(setLoadError("Unable to fetch markets."));
      console.error(reason);
    });

    const providerFetch = fetchAllProviders().then((providers) => {
      const physicians = new Array<ProviderOption>();
      const others = new Array<ProviderOption>();

      const filteredProviders = providers.filter(provider => {
        return provider.active;
      });

      filteredProviders.forEach(provider => {
        const optionData = {
          id: provider.providerId,
          npi: provider.providerNPI,
          firstName: provider.firstName,
          lastName: provider.lastName,
          suffix: provider.suffix,
          fullName: `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`,
          avatarUrl: provider.avatarUrl,
          markets: provider.providerMarkets.map(({marketCode}) => marketCode),
        };

        const providerCategory = provider.suffix
          ? getProviderCategory(provider)
          : undefined;

        /**
         * If the provider doesn't have a suffix or the suffix doesn't match our predefined list,
         * remove it from the set of options used throughout pods.
         */
        if (providerCategory === undefined) return;

        if (providerCategory === 'Physician') {
          physicians.push(optionData);
        } else {
          others.push(optionData);
        }
      });
      
      dispatch(setProviderList(physicians, others));
    }).catch(reason => {
      dispatch(setLoadError("Unable to fetch providers."));
      console.error(reason);
    });

    // If both promises resolve, set ready to true.
    Promise.all([marketFetch, providerFetch])
      .then(() => {
        dispatch(setReady(true));
      });
  }, [dispatch]);

  useEffect(() => {
    const isManager = authState?.accessToken
      ? isOpsManager(authState.accessToken)
      : false;

    dispatch(setUserIsManager(isManager));
  }, [authState, dispatch]);

  if (loadError) {
    return (
      <PodPlaceholder type="error" iconName="Cancel" text={loadError} />
    );
  }

  return (
    <PodStateContext.Provider value={{useContextSelector: useSelector, contextDispatch: dispatch}}>
      <DocumentTitle title="Pods"/>
      <Routes>
        <Route index element={<PodSearchPage />} />
        <Route path=':id' element={<PodPage />} />
      </Routes>
    </PodStateContext.Provider>
  );
}

export default PodRouter;
